/**
 * フィールドコード（#3175）のユーティリティ。
 *
 * 背景:
 * 項目名は OCR の精度改善のために頻繁に書き換えられるため、計算フィールド（#3145）や
 * 指定フォーマット出力（#3070）で項目を指定する識別子には使えない。項目名とは別に
 * 「簡単に変わらない識別子」としてフィールドコードを持たせる。
 *
 * 方針:
 * - 全項目（親・子）が持てる。特定機能の専用ではない（参照される側にも必要なため）
 * - 初期値は項目名から自動採番し、重複時は `_2` `_3` … の連番を付ける
 * - 項目名を変更してもフィールドコードは追随しない（追随すると参照が壊れるため）
 * - 未設定の既存項目は編集画面を開いたときに補完する（Firestore への書き込みは保存時）
 *
 * 注: 通常の CSV/Excel 出力のヘッダー名は別機能（#3158 CSV出力名）で扱う。ここでは関与しない。
 *
 * ⚠️ このファイルの採番ロジックは functions/src/scripts/migrate-field-codes.ts にも
 *    同じ内容で存在する（main と functions はコードを共有していないため）。
 *    片方だけ変更すると、マイグレーション済みの値と画面上で採番される値がズレる。
 *    変更するときは必ず両方を直すこと。
 */
import { FIELD_CODE_MAX_LENGTH, FIELD_CODE_SUFFIX_MAX } from '../../constants/enum';
import { type ItemListType, type ItemListChildType } from '../../types/common';
import { renameFieldCodeInFormula } from '../calculation';

/** 項目名が記号だけなどでフィールドコードを作れないときの基底値 */
const DEFAULT_FIELD_CODE_BASE = '項目';

/**
 * 明細（テーブル型）の子項目のコードで、親と子を区切る文字。
 * `明細.品名` の形にすることで、どの明細の子かが記法だけで分かる。
 * 項目名自体に `.` を含むもの（「No.」など）があるため、
 * 解析するときは「最初の区切り文字まで」を親として扱うこと。
 */
export const FIELD_CODE_SEPARATOR = '.';

/**
 * フィールドコードに使えない文字。
 * 波括弧は参照記法 `{...}` と衝突し、改行・空白（半角/全角 U+3000）は識別子として不適なため除去する。
 */
const FORBIDDEN_CHARS_PATTERN = /[{}\r\n\s\u3000]/g;

/**
 * 識別子として意味を持つ文字（英数字・ひらがな・カタカナ・漢字・長音）。
 * これを1文字も含まない項目名（「？」「※」など記号だけ）は、そのままコードにすると
 * 計算式が `{？}` `{？_2}` となり人間が判別できないため、既定の基底値へ倒す。
 */
const MEANINGFUL_CHARS_PATTERN = /[0-9A-Za-zぁ-ゖァ-ヺー一-鿿０-９Ａ-Ｚａ-ｚ]/;

export type FieldCodeValidationError =
  | 'empty' // 空文字
  | 'tooLong' // 最大長超過
  | 'invalidChar' // 使用できない文字を含む
  | 'duplicated'; // 同一パターン内で重複

/** バリデーションエラーの表示文言 */
export const fieldCodeErrorMessages: Record<FieldCodeValidationError, string> = {
  empty: 'フィールドコードを入力してください。',
  tooLong: `フィールドコードは${FIELD_CODE_MAX_LENGTH}文字以内で入力してください。`,
  invalidChar: 'フィールドコードに空白と波括弧（{ }）は使用できません。',
  duplicated: '同じフィールドコードが既に使われています。',
};

/** コードポイント単位で最大長に切り詰める（サロゲートペア対応） */
const truncateFieldCode = (value: string, maxLength: number): string =>
  Array.from(value).slice(0, maxLength).join('');

/**
 * 項目名からフィールドコードの基底値を作る。
 * 日本語をそのまま使えるようにし、記法と衝突する文字だけを除去する。
 * kintone のフィールドコードも日本語可のため、それに倣う。
 */
export const sanitizeFieldCode = (itemName: string): string => {
  const sanitized = truncateFieldCode(
    itemName.replace(FORBIDDEN_CHARS_PATTERN, ''),
    FIELD_CODE_MAX_LENGTH,
  );
  // 記号だけの項目名（「？」など）は識別子として機能しないため既定の基底値へ倒す
  if (!MEANINGFUL_CHARS_PATTERN.test(sanitized)) return DEFAULT_FIELD_CODE_BASE;
  return sanitized;
};

/**
 * 既存コードと衝突しないフィールドコードを採番する。
 * 重複時は `_2` `_3` … を付ける。最大長を超える場合は基底値を切り詰めてから付ける。
 *
 * 明細（テーブル型）の子項目は `親のコード.子の名前` の形にする（例: `明細.品名`）。
 * どの明細の子かが記法だけで分かり、単一項目と同名でも衝突しなくなる。
 *
 * @param itemName 元にする項目名
 * @param usedCodes 既に使われているコードの集合
 * @param parentCode 子項目の場合に前置する親のフィールドコード
 */
export const generateFieldCode = (
  itemName: string,
  usedCodes: ReadonlySet<string>,
  parentCode?: string,
): string => {
  const own = sanitizeFieldCode(itemName);
  // 親のコードが最大長を圧迫しないよう、前置したうえで全体を切り詰める
  const base = parentCode
    ? truncateFieldCode(`${parentCode}${FIELD_CODE_SEPARATOR}${own}`, FIELD_CODE_MAX_LENGTH)
    : own;
  if (!usedCodes.has(base)) return base;

  for (let sequence = 2; sequence <= FIELD_CODE_SUFFIX_MAX; sequence += 1) {
    const suffix = `_${sequence}`;
    // 連番を足しても最大長を超えないよう、基底値側を切り詰める
    const candidate = `${truncateFieldCode(base, FIELD_CODE_MAX_LENGTH - suffix.length)}${suffix}`;
    if (!usedCodes.has(candidate)) return candidate;
  }

  // ここまで衝突する場合は連番の上限に達している。呼び出し側で重複エラーとして扱われる
  return base;
};

/**
 * フィールドコード単体の形式を検証する（重複は含まない）。
 * 重複判定は呼び出し側が持つ集合と突き合わせるため validateFieldCode を使う。
 */
export const validateFieldCodeFormat = (code: string): FieldCodeValidationError | null => {
  if (code.trim() === '') return 'empty';
  if (Array.from(code).length > FIELD_CODE_MAX_LENGTH) return 'tooLong';
  if (FORBIDDEN_CHARS_PATTERN.test(code)) {
    FORBIDDEN_CHARS_PATTERN.lastIndex = 0; // g フラグ付き正規表現の状態を持ち越さない
    return 'invalidChar';
  }
  FORBIDDEN_CHARS_PATTERN.lastIndex = 0;
  return null;
};

/**
 * フィールドコードを形式・重複の両面で検証する。
 * @param usedCodes 自分以外の項目が使っているコードの集合
 */
export const validateFieldCode = (
  code: string,
  usedCodes: ReadonlySet<string>,
): FieldCodeValidationError | null => {
  const formatError = validateFieldCodeFormat(code);
  if (formatError) return formatError;
  return usedCodes.has(code.trim()) ? 'duplicated' : null;
};

/**
 * 項目リスト内の全フィールドコードを集める（親・子の両方。空は含めない）。
 * 論理削除（is_active === false）された項目は、画面に出ず採番の衝突対象にもならないため除外する。
 */
export const collectFieldCodes = (itemList: ItemListType[]): Set<string> => {
  const codes = new Set<string>();
  itemList.forEach(item => {
    if (item.is_active === false) return;
    if (item.field_code) codes.add(item.field_code);
    item.children?.forEach(child => {
      if (child.is_active === false) return;
      if (child.field_code) codes.add(child.field_code);
    });
  });
  return codes;
};

/**
 * 項目リスト内でフィールドコードが未設定の項目に、項目名を元にした値を補完する。
 * 既存パターン（フィールドコード導入前に作られたもの）を編集画面で開いたときに使う。
 * 既に設定済みのコードは書き換えない（参照が壊れるため）。
 * 論理削除された項目には採番しない。
 *
 * 副作用を持たせないよう、新しい配列を返す。
 */
export const fillMissingFieldCodes = (itemList: ItemListType[]): ItemListType[] => {
  const usedCodes = collectFieldCodes(itemList);

  const resolve = (current: string | undefined, name: string, parentCode?: string): string => {
    if (current) return current;
    const generated = generateFieldCode(name, usedCodes, parentCode);
    usedCodes.add(generated);
    return generated;
  };

  return itemList.map(item => {
    if (item.is_active === false) return item;
    // 子のコードは親のコードを前置するため、親を先に確定させる
    const parentCode = resolve(item.field_code, item.parentValue);
    return {
      ...item,
      field_code: parentCode,
      children: (item.children ?? []).map(child => {
        if (child.is_active === false) return child;
        return {
          ...child,
          field_code: resolve(child.field_code, child.childValue, parentCode),
        };
      }),
    };
  });
};

/**
 * 項目リスト全体のフィールドコードを検証し、最初に見つかったエラーを返す。
 * パターン保存時のバリデーションに使う。
 */
export const validateFieldCodesInItemList = (
  itemList: ItemListType[],
): { itemName: string; error: FieldCodeValidationError } | null => {
  const seen = new Set<string>();

  const check = (
    code: string | undefined,
    name: string,
  ): { itemName: string; error: FieldCodeValidationError } | null => {
    // 未設定は補完前の既存項目とみなしてエラーにしない（保存時に fillMissingFieldCodes を通す）
    if (code === undefined || code === '') return null;
    const error = validateFieldCode(code, seen);
    if (error) return { itemName: name, error };
    seen.add(code.trim());
    return null;
  };

  for (const item of itemList) {
    if (item.is_active === false) continue;
    const parentError = check(item.field_code, item.parentValue);
    if (parentError) return parentError;

    for (const child of item.children ?? []) {
      if (child.is_active === false) continue;
      const childError = check(child.field_code, child.childValue);
      if (childError) return childError;
    }
  }

  return null;
};

/** 子項目を含めた全項目を「フィールドコード → 項目名」で引ける形にする（UI の表示・案内用） */
export const buildFieldCodeMap = (
  itemList: ItemListType[],
): Map<string, { itemName: string; isChild: boolean }> => {
  const map = new Map<string, { itemName: string; isChild: boolean }>();
  itemList.forEach((item: ItemListType) => {
    if (item.is_active === false) return;
    if (item.field_code) map.set(item.field_code, { itemName: item.parentValue, isChild: false });
    item.children?.forEach((child: ItemListChildType) => {
      if (child.is_active === false) return;
      if (child.field_code)
        map.set(child.field_code, { itemName: child.childValue, isChild: true });
    });
  });
  return map;
};

/**
 * フィールドコード変更時に、そのコードを参照しているすべての計算式を書き換える（#3145。設計書 §5.2）。
 * トークン単位で置換するため、文字列リテラル内の波括弧は書き換えない
 * （renameFieldCodeInFormula に委譲。トークン化できない式は元のまま）
 */
export const renameFieldCodeReferences = (
  itemList: ItemListType[],
  oldCode: string,
  newCode: string,
): ItemListType[] => {
  return itemList.map(item => ({
    ...item,
    ...(item.formula ? { formula: renameFieldCodeInFormula(item.formula, oldCode, newCode) } : {}),
    children: (item.children ?? []).map(child =>
      child.formula
        ? { ...child, formula: renameFieldCodeInFormula(child.formula, oldCode, newCode) }
        : child,
    ),
  }));
};
